// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { Box } from '@mui/material';
// Components, Layouts, Pages
import { BaseInput } from '~/components/common';
// Others
import { IBaseAddress } from '~/utils/interface/common';
import { PlaceAddressEnum } from '~/utils/enum/common';
import { googleMapApiKey } from '~/utils/constants/env';
import { DEFAULT_GG_MAP_LOAD_SCRIPT_LIB, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseInputAddress.module.scss';
import './BaseInputAddress.scss';
import { icons } from '~/assets';

type Props = {
  label: string;
  value?: string;
  height?: number;
  showIcon?: boolean;
  borderRadius?: number | string;
  placeholder?: string;
  errorMessage?: string;
  required?: boolean;
  onChange?: (value: string) => void;
  onSelectAddress?: (value: IBaseAddress) => void;
};

const cx = classNames.bind(styles);

const BaseInputAddress = (props: Props) => {
  //#region Destructuring Props
  const {
    label,
    value,
    placeholder = EMPTY_STRING,
    showIcon = false,
    errorMessage,
    required,
    height,
    borderRadius,
    onChange,
    onSelectAddress,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [autoCompleteAddress, setAutoCompleteAddress] = useState<google.maps.places.Autocomplete>();
  const [inputValue, setInputValue] = useState<string>(value || EMPTY_STRING);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    setInputValue(value || EMPTY_STRING);
  }, [value]);
  //#endregion Implement Hook

  //#region Handle Function
  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    setAutoCompleteAddress(autocomplete);
  };

  const onPlaceChanged = () => {
    if (!autoCompleteAddress) return;

    const place = autoCompleteAddress?.getPlace();

    const addressComponents = place.address_components || [];

    const getAddressComponent = (type: string) => {
      const value = addressComponents.find((address) => address.types.includes(type));
      if (!value) return EMPTY_STRING;

      return type === PlaceAddressEnum.COUNTRY ? value.long_name : value.short_name;
    };

    const address = place.formatted_address || EMPTY_STRING;
    const city = getAddressComponent(PlaceAddressEnum.LOCALITY);
    const state = getAddressComponent(PlaceAddressEnum.STATE);
    const postalCode = getAddressComponent(PlaceAddressEnum.ZIP_CODE);
    const country = getAddressComponent(PlaceAddressEnum.COUNTRY);
    const unitDetail = getAddressComponent(PlaceAddressEnum.UNIT);

    const location = place.geometry?.location;
    const lat = location?.lat().toString();
    const lng = location?.lng().toString();

    const updatedAddress: IBaseAddress = {
      address,
      unitDetail,
      city,
      state,
      country,
      postalCode,
      lat,
      lng,
    };

    setInputValue(updatedAddress.address as string);
    onSelectAddress && onSelectAddress(updatedAddress);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (!newValue) {
      onSelectAddress && onSelectAddress({});
    }

    setInputValue(newValue);
    onChange && onChange(newValue);
  };
  //#endregion Handle Function

  return (
    <Box id='baseInputAddressComponent'>
      {isLoaded && (
        <Autocomplete onPlaceChanged={onPlaceChanged} onLoad={onLoad}>
          <BaseInput
            {...(showIcon && {
              startAdornment: <img className={cx('icon')} src={icons.commonIconSearch} alt={t('common_alt_img')} />,
            })}
            height={height}
            required={required}
            borderRadius={borderRadius}
            placeholder={placeholder}
            label={label}
            value={inputValue}
            onChange={handleInputChange}
            errorMessage={errorMessage}
          />
        </Autocomplete>
      )}
    </Box>
  );
};

export default memo(BaseInputAddress);
